import React, { useCallback, useEffect, useState } from 'react';
import { MapPinned, Plus, Edit2, Trash2, Save, X, AlertTriangle } from 'lucide-react';
import ConfirmModal from './ConfirmModal';

// Sites (provozovny) of a customer — places where its machines are installed.
const EMPTY = { name: '', street: '', city: '', postal_code: '' };
const input = { width: '100%', padding: '8px 10px', border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const btn = (primary) => ({
  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
  cursor: 'pointer', border: primary ? 'none' : '1px solid #E0E0E0', backgroundColor: primary ? '#E53935' : '#FFF', color: primary ? '#FFF' : '#333',
});
const iconBtn = (bg) => ({ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: bg, cursor: 'pointer', display: 'flex' });

async function api(url, options) {
  const res = await fetch(url, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...options });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) throw new Error(json.details?.[0]?.msg || json.error || `Chyba ${res.status}`);
  return json.data;
}

const SiteForm = ({ value, onChange, onSubmit, onCancel, busy }) => {
  const field = (key, label, placeholder, flex = '1 1 160px') => (
    <label style={{ flex, fontSize: '12px', color: '#333' }}>{label}
      <input style={{ ...input, marginTop: '4px' }} value={value[key] || ''} placeholder={placeholder} onChange={(e) => onChange({ ...value, [key]: e.target.value })} />
    </label>
  );
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'end', padding: '12px', backgroundColor: '#FFF8E1', borderRadius: '10px' }}>
      {field('name', 'Název *', 'např. Plzeň – Americká')}
      {field('street', 'Ulice a č. p.', 'Americká 5')}
      {field('city', 'Město *', 'Plzeň')}
      {field('postal_code', 'PSČ', '301 00', '0 1 100px')}
      <button type="submit" style={btn(true)} disabled={busy}><Save size={14} /> Uložit</button>
      <button type="button" style={btn(false)} onClick={onCancel}><X size={14} /> Zrušit</button>
    </form>
  );
};

const CustomerSites = ({ customerId, onChanged }) => {
  const [sites, setSites] = useState([]);
  const [form, setForm] = useState(null); // { id?: number, ...fields }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try { setSites(await api(`/api/customers/${customerId}/sites`)); } catch (err) { setError(err.message); }
  }, [customerId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setError('');
    try {
      const { id, ...data } = form;
      await api(`/api/customers/${customerId}/sites${id ? `/${id}` : ''}`, { method: id ? 'PUT' : 'POST', body: JSON.stringify(data) });
      setForm(null);
      await load();
      onChanged?.();
    } catch (err) { setError(`Uložení se nezdařilo: ${err.message}`); } finally { setBusy(false); }
  };

  const remove = async () => {
    const site = toDelete;
    setToDelete(null);
    try {
      await api(`/api/customers/${customerId}/sites/${site.id}`, { method: 'DELETE' });
      await load();
      onChanged?.();
    } catch (err) { setError(`Smazání se nezdařilo: ${err.message}`); }
  };

  return (
    <div style={{ backgroundColor: '#FFF', borderRadius: '12px', border: '1px solid #E0E0E0', padding: '24px', marginTop: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '12px', flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#333', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
          <MapPinned color="#E53935" size={20} /> Provozovny ({sites.length})
        </h2>
        {!form && <button type="button" style={btn(true)} onClick={() => setForm({ ...EMPTY })}><Plus size={16} /> Přidat provozovnu</button>}
      </div>
      <p style={{ fontSize: '13px', color: '#666', margin: '0 0 16px' }}>
        Místa, kde stojí trenažéry. Trenažér bez provozovny se na mapě zobrazuje na adrese klienta.
      </p>

      {form && !form.id && <div style={{ marginBottom: '12px' }}><SiteForm value={form} onChange={setForm} onSubmit={save} onCancel={() => setForm(null)} busy={busy} /></div>}

      {sites.length === 0 && !form && <p style={{ textAlign: 'center', color: '#999', padding: '16px' }}>Zatím žádné provozovny</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {sites.map((s) => (form?.id === s.id ? (
          <SiteForm key={s.id} value={form} onChange={setForm} onSubmit={save} onCancel={() => setForm(null)} busy={busy} />
        ) : (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', backgroundColor: '#F9F9F9', borderRadius: '10px', border: '1px solid #EEE' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: '#333' }}>{s.name}</div>
              <div style={{ fontSize: '13px', color: '#666' }}>
                {[s.street, [s.postal_code, s.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '—'} · trenažérů: {s.machine_count}
              </div>
              {(s.geocode_precision === 'city' || ['not_found', 'error'].includes(s.geocode_status)) && (
                <div style={{ fontSize: '12px', color: '#F57C00', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                  <AlertTriangle size={12} /> {s.geocode_precision === 'city' ? 'Na mapě jen přibližně (doplňte ulici)' : 'Adresu se nepodařilo najít na mapě'}
                </div>
              )}
            </div>
            <button type="button" aria-label={`Upravit ${s.name}`} style={iconBtn('#F5F5F5')} onClick={() => setForm({ id: s.id, name: s.name, street: s.street || '', city: s.city || '', postal_code: s.postal_code || '' })}><Edit2 size={16} color="#666" /></button>
            <button type="button" aria-label={`Smazat ${s.name}`} style={iconBtn('#FFEBEE')} onClick={() => setToDelete(s)}><Trash2 size={16} color="#E53935" /></button>
          </div>
        )))}
      </div>
      {error && <p role="alert" style={{ color: '#D32F2F', fontSize: '13px', margin: '12px 0 0' }}>{error}</p>}

      <ConfirmModal show={!!toDelete} title="Smazat provozovnu?" danger confirmText="Smazat"
        message={toDelete ? `Provozovna „${toDelete.name}“ bude smazána. Trenažéry, které k ní patří (${toDelete.machine_count}), se na mapě přesunou na adresu klienta.` : ''}
        onConfirm={remove} onCancel={() => setToDelete(null)} />
    </div>
  );
};

export default CustomerSites;
